import * as Yup from 'yup';

import type { ProgramQuestion } from 'types';

export type ProgramQuestionValues = Record<string, string | string[]>;

function textAnswer(values: ProgramQuestionValues, name: string): string {
    const raw = values[name];
    return typeof raw === 'string' ? raw.trim() : '';
}

export function optionsForQuestion(
    question: ProgramQuestion,
    values: ProgramQuestionValues
): string[] {
    const grouped = question.options_by;
    const parentName = question.depends_on?.name;
    if (grouped && parentName) {
        const parent = textAnswer(values, parentName);
        return grouped[parent] ?? [];
    }
    return question.options ?? [];
}

export function isQuestionActive(
    question: ProgramQuestion,
    values: ProgramQuestionValues,
    questions: ProgramQuestion[] = []
): boolean {
    if (!question.depends_on) {
        return true;
    }
    const parentQuestion = questions.find((item) => item.name === question.depends_on?.name);
    if (parentQuestion && !isQuestionActive(parentQuestion, values, questions)) {
        return false;
    }
    const raw = values[question.depends_on.name];
    const selected = Array.isArray(raw) ? raw : [textAnswer(values, question.depends_on.name)];
    const anyOf = question.depends_on.any_of;
    const expected = anyOf?.length ? anyOf : [question.depends_on.value ?? ''];
    return selected.some((item) => expected.some((option) => option.trim() === item.trim()));
}

export function visibleQuestions(
    questions: ProgramQuestion[],
    values: ProgramQuestionValues
): ProgramQuestion[] {
    return questions.filter((question) => isQuestionActive(question, values, questions));
}

export function initialAnswers(questions: ProgramQuestion[]): ProgramQuestionValues {
    return Object.fromEntries(
        questions.map((question) => {
            if (question.type === 'checkboxes') {
                return [question.name, Array.isArray(question.value) ? question.value : []];
            }
            return [question.name, typeof question.value === 'string' ? question.value : ''];
        })
    );
}

export function answersForSave(
    questions: ProgramQuestion[],
    values: ProgramQuestionValues
): ProgramQuestionValues {
    const payload: ProgramQuestionValues = {};
    for (const question of questions) {
        if (!isQuestionActive(question, values, questions)) {
            continue;
        }
        const raw = values[question.name];
        if (question.type === 'checkboxes') {
            const selected = (Array.isArray(raw) ? raw : [])
                .map((item) => item.trim())
                .filter(Boolean);
            if (selected.length > 0) {
                payload[question.name] = selected;
            }
            continue;
        }
        const value = typeof raw === 'string' ? raw.trim() : '';
        if (value) {
            payload[question.name] = value;
        }
    }
    return payload;
}

const EARLIEST_DATE = '1921-01-01';

function todayIso(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

function tomorrowIso(today: string): string {
    const [year, month, day] = today.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    const nextMonth = String(next.getUTCMonth() + 1).padStart(2, '0');
    const nextDay = String(next.getUTCDate()).padStart(2, '0');
    return `${next.getUTCFullYear()}-${nextMonth}-${nextDay}`;
}

function isRealIsoDate(value: string): boolean {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (
        parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day
    );
}

function dateWindow(
    question: ProgramQuestion,
    today: string
): { minDate?: string; maxDate?: string } {
    let minDate = question.min_date || undefined;
    let maxDate = question.max_date || undefined;
    if (question.after_today) {
        const tomorrow = tomorrowIso(today);
        if (!minDate || tomorrow > minDate) {
            minDate = tomorrow;
        }
    }
    if (!question.min_date && !question.max_date && !question.after_today) {
        minDate = EARLIEST_DATE;
        maxDate = today;
    }
    return { minDate, maxDate };
}

function dateInRange(question: ProgramQuestion, value: string, today: string): boolean {
    if (!isRealIsoDate(value)) {
        return false;
    }
    const { minDate, maxDate } = dateWindow(question, today);
    if (minDate && value < minDate) {
        return false;
    }
    if (maxDate && value > maxDate) {
        return false;
    }
    return true;
}

function answerSchema(
    question: ProgramQuestion,
    today: string,
    questions: ProgramQuestion[]
): Yup.AnySchema {
    if (question.type === 'checkboxes') {
        const options = question.options ?? [];
        let checks = Yup.array()
            .of(Yup.string().required())
            .test('option', 'Choose one of the options', (value) => {
                if (!value) {
                    return true;
                }
                return value.every((item) => options.includes(String(item)));
            });
        if (question.required) {
            checks = checks.min(1, 'Required');
        }
        return checks;
    }

    let field = Yup.string().trim();
    if (question.max_length) {
        field = field.max(question.max_length, `Use at most ${question.max_length} characters`);
    }
    if (question.digits) {
        field = field.test('digits', 'Enter a non-negative whole number', (value) => {
            const text = String(value ?? '').trim();
            if (!text) {
                return true;
            }
            const compact = text.replace(/,/g, '').replace(/\s/g, '');
            return /^(?:0|[1-9]\d{0,9})$/.test(compact);
        });
    }
    if (question.matches) {
        const otherName = question.matches;
        const message = question.match_message || 'Values must match';
        field = field.test('matches', message, function (value) {
            const other = String(this.parent?.[otherName] ?? '').trim();
            return String(value ?? '').trim() === other;
        });
    }
    if (question.type === 'date') {
        field = field.test('date', 'Use a date', (value) => {
            if (!value) {
                return true;
            }
            return isRealIsoDate(value);
        });
        field = field.test('range', 'Date is out of range', (value) => {
            if (!value || !isRealIsoDate(value)) {
                return true;
            }
            return dateInRange(question, value, today);
        });
    }
    const otherDateParsed = (name: string, value: string): boolean => {
        if (!value || !isRealIsoDate(value)) {
            return false;
        }
        const otherQuestion = questions.find((item) => item.name === name);
        return !otherQuestion || dateInRange(otherQuestion, value, today);
    };
    if (question.before) {
        const otherName = question.before;
        const message = question.before_message || 'Enter an earlier date';
        field = field.test('before', message, function (value) {
            const other = String(this.parent?.[otherName] ?? '').trim();
            const current = String(value ?? '').trim();
            if (!otherDateParsed(otherName, other) || !dateInRange(question, current, today)) {
                return true;
            }
            return current < other;
        });
    }
    if (question.after) {
        const otherName = question.after;
        const message = question.after_message || 'Enter a later date';
        field = field.test('after', message, function (value) {
            const other = String(this.parent?.[otherName] ?? '').trim();
            const current = String(value ?? '').trim();
            if (!otherDateParsed(otherName, other) || !dateInRange(question, current, today)) {
                return true;
            }
            return current > other;
        });
    }
    if (question.type === 'select' && (question.options?.length || question.options_by)) {
        field = field.test('option', 'Choose one of the options', function (value) {
            const options = optionsForQuestion(question, this.parent ?? {});
            const text = (value ?? '').trim();
            if (!options.length) {
                return text === '';
            }
            return text === '' || options.includes(text);
        });
    }
    if (question.required) {
        field = field.required('Required');
    }
    return field;
}

export function validationSchema(questions: ProgramQuestion[]) {
    const shape: Record<string, Yup.ISchema<unknown>> = {};
    const today = todayIso();

    for (const question of questions) {
        if (!question.depends_on) {
            shape[question.name] = answerSchema(question, today, questions);
            continue;
        }
        shape[question.name] = Yup.lazy((_value, context) => {
            if (isQuestionActive(question, context.parent ?? {}, questions)) {
                return answerSchema(question, today, questions);
            }
            return Yup.mixed().notRequired();
        });
    }

    return Yup.object(shape);
}

export function questionWarningCount(
    questions: ProgramQuestion[],
    values: ProgramQuestionValues,
    errors: Record<string, unknown> = {}
): number {
    const invalidNames = new Set(Object.keys(errors).filter((name) => Boolean(errors[name])));
    try {
        validationSchema(questions).validateSync(values, { abortEarly: false });
    } catch (error) {
        if (!(error instanceof Yup.ValidationError)) {
            throw error;
        }
        for (const issue of error.inner) {
            if (issue.path) {
                invalidNames.add(issue.path.split('[')[0]);
            }
        }
    }
    return visibleQuestions(questions, values).filter((question) => invalidNames.has(question.name))
        .length;
}

/** Field messages from PUT /program-questions, which returns `{ errors: { name: message } }`. */
export function serverFieldErrors(payload: unknown): Record<string, string> {
    if (!payload || typeof payload !== 'object') {
        return {};
    }
    const errors = (payload as { errors?: unknown }).errors;
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
        return {};
    }
    const fields: Record<string, string> = {};
    for (const [key, value] of Object.entries(errors)) {
        if (typeof value === 'string' && value.trim()) {
            fields[key] = value.trim();
        } else if (Array.isArray(value)) {
            const first = value.find((item) => typeof item === 'string' && item.trim());
            if (typeof first === 'string') {
                fields[key] = first.trim();
            }
        }
    }
    return fields;
}
